import {
  AccountInfo,
  PopupRequest,
  PublicClientApplication,
  SilentRequest
} from '@azure/msal-browser';
import { v4 as uuidv4 } from 'uuid';
import {
  fetchBrowserFileFromUrl,
  inferMimeFromFilename
} from '../cloudPickerFileUtils';
import type { CloudPickerAdapter, CloudPickerFile } from './types';
import {
  MICROSOFT_GRAPH,
  MSAL_SCOPES,
  ONEDRIVE_CONSUMER
} from './oneDrivePickerConstants';
import {
  buildOneDrivePickerUrl,
  deriveSharePointBaseUrlFromUsername,
  isConsumerOneDriveResource,
  isPersonalMicrosoftAccount
} from './oneDrivePickerUtils';
import type { PickerTarget } from './oneDrivePickerUtils';
import {
  MEDIA_UPLOAD_ALL_EXTENSIONS,
  MEDIA_UPLOAD_IMAGE_ONLY_EXTENSIONS
} from '../../../config/constants';

export interface OneDrivePickerOptions {
  appId: string;
  authority: string;
  allowVideoAndPdf: boolean;
  locale?: string;
  forceAccountSelect?: boolean;
}

interface OneDriveSessionContext {
  appId: string;
  authority: string;
  target: PickerTarget;
}

interface OneDriveDrive {
  webUrl?: string;
}

interface OneDriveApiErrorResponse {
  error?: {
    code?: string;
    message?: string;
  };
}

interface OneDriveFileItem {
  id: string;
  name?: string;
  size?: number;
  folder?: unknown;
  downloadUrl?: string;
  urls?: {
    download?: string;
  };
  '@microsoft.graph.downloadUrl'?: string;
  file?: {
    mimeType?: string;
  };
  parentReference?: {
    driveId?: string;
  };
}

interface OneDrivePickerCommand {
  command: string;
  type?: string;
  resource?: string;
  items?: OneDriveFileItem[];
}

type PickerPromiseHandlers = {
  resolve: (files: CloudPickerFile[]) => void;
  reject: (error: Error) => void;
};

type OneDrivePickerErrorCode =
  | 'drive_not_found'
  | 'access_denied'
  | 'invalid_work_base_url';

class OneDrivePickerError extends Error {
  code: OneDrivePickerErrorCode;

  constructor(code: OneDrivePickerErrorCode, message: string) {
    super(message);
    this.name = 'OneDrivePickerError';
    this.code = code;
  }
}

export const isOneDrivePickerError = (
  error: unknown
): error is OneDrivePickerError => error instanceof OneDrivePickerError;

let cachedMsalApp:
  | {
      appId: string;
      authority: string;
      redirectUri: string;
      promise: Promise<PublicClientApplication>;
    }
  | undefined;

const buildAuthorityUrl = (authority: string) =>
  authority.startsWith('https://')
    ? authority
    : `https://login.microsoftonline.com/${authority}`;

const getMsalApp = async (appId: string, authority: string) => {
  const authorityUrl = buildAuthorityUrl(authority);
  const redirectUri = window.location.origin;

  if (
    !cachedMsalApp ||
    cachedMsalApp.appId !== appId ||
    cachedMsalApp.authority !== authorityUrl ||
    cachedMsalApp.redirectUri !== redirectUri
  ) {
    cachedMsalApp = {
      appId,
      authority: authorityUrl,
      redirectUri,
      promise: (async () => {
        const app = new PublicClientApplication({
          auth: {
            clientId: appId,
            authority: authorityUrl,
            redirectUri
          },
          cache: {
            cacheLocation: 'sessionStorage'
          }
        });

        await app.initialize();

        if (!app.getActiveAccount()) {
          const account = app.getAllAccounts()[0];
          if (account) {
            app.setActiveAccount(account);
          }
        }

        return app;
      })()
    };
  }

  return await cachedMsalApp.promise;
};

const getPreferredAccount = (app: PublicClientApplication) =>
  app.getActiveAccount() || app.getAllAccounts()[0] || null;

const signIn = async (
  appId: string,
  authority: string
): Promise<AccountInfo> => {
  const app = await getMsalApp(appId, authority);
  const result = await app.loginPopup({
    scopes: [MSAL_SCOPES.userRead],
    prompt: 'select_account'
  });

  if (result.account) {
    app.setActiveAccount(result.account);
    return result.account;
  }

  const account = getPreferredAccount(app);
  if (!account) {
    throw new Error('Unable to determine the Microsoft account for OneDrive.');
  }

  return account;
};

const ensureMicrosoftAccount = async (
  appId: string,
  authority: string
): Promise<AccountInfo> => {
  const app = await getMsalApp(appId, authority);
  const account = getPreferredAccount(app);

  if (account) {
    return account;
  }

  return await signIn(appId, authority);
};

const acquireAccessToken = async ({
  appId,
  authority,
  scopes,
  requestAuthority
}: {
  appId: string;
  authority: string;
  scopes: string[];
  requestAuthority?: string;
}) => {
  const app = await getMsalApp(appId, authority);
  let account = getPreferredAccount(app);

  if (!account) {
    account = await ensureMicrosoftAccount(appId, authority);
  }

  const authParams: SilentRequest = {
    scopes,
    account: account || undefined,
    authority: requestAuthority
  };

  try {
    const silentResult = await app.acquireTokenSilent(authParams);

    if (silentResult.account) {
      app.setActiveAccount(silentResult.account);
    }

    return silentResult.accessToken;
  } catch (error) {
    console.warn('Silent Microsoft token acquisition failed.', {
      scopes,
      requestAuthority,
      error
    });
  }

  const popupParams: PopupRequest = {
    scopes,
    account: account || undefined,
    authority: requestAuthority,
    prompt: account ? undefined : 'select_account'
  };

  const popupResult = await app.acquireTokenPopup(popupParams);

  if (popupResult.account) {
    app.setActiveAccount(popupResult.account);
  }

  return popupResult.accessToken;
};

const readApiErrorResponse = async (response: Response) => {
  try {
    return (await response.json()) as OneDriveApiErrorResponse;
  } catch (error) {
    return undefined;
  }
};

const getBusinessBaseUrlFromGraph = async (
  appId: string,
  authority: string
) => {
  const graphToken = await acquireAccessToken({
    appId,
    authority,
    scopes: [MICROSOFT_GRAPH.defaultScope]
  });

  const response = await fetch(
    `${MICROSOFT_GRAPH.baseUrl}/me/drive?$select=webUrl`,
    {
      headers: {
        Authorization: `Bearer ${graphToken}`
      }
    }
  );

  if (!response.ok) {
    const errorData = await readApiErrorResponse(response);

    if (
      response.status === 404 &&
      errorData?.error?.code === 'itemNotFound'
    ) {
      throw new OneDrivePickerError(
        'drive_not_found',
        'This Microsoft account does not have a OneDrive available. Try an account with OneDrive enabled.'
      );
    }

    if (response.status === 403) {
      throw new OneDrivePickerError(
        'access_denied',
        'Microsoft denied access to OneDrive. Check the app permissions and consent.'
      );
    }

    const apiMessage = errorData?.error?.message
      ? ` ${errorData.error.message}`
      : '';

    throw new Error(
      `Failed to resolve the OneDrive base URL. Status: ${response.status}.${apiMessage}`
    );
  }

  const driveData: OneDriveDrive = await response.json();
  if (!driveData.webUrl) {
    throw new Error('No OneDrive base URL was returned for the current user.');
  }

  return new URL(driveData.webUrl).origin;
};

const resolvePickerTarget = async ({
  appId,
  authority
}: Pick<OneDrivePickerOptions, 'appId' | 'authority'>): Promise<PickerTarget> => {
  const account = await ensureMicrosoftAccount(appId, authority);

  if (isPersonalMicrosoftAccount(account)) {
    return {
      accountType: 'personal',
      baseUrl: ONEDRIVE_CONSUMER.baseUrl,
      pickerPathOverride: ONEDRIVE_CONSUMER.pickerPath
    };
  }

  try {
    return {
      accountType: 'work',
      baseUrl: deriveSharePointBaseUrlFromUsername(account.username)
    };
  } catch (error) {
    console.warn(
      'Unable to derive the SharePoint base URL from the account username. Falling back to Graph discovery.',
      error
    );

    const fallbackBaseUrl = await getBusinessBaseUrlFromGraph(appId, authority);

    return {
      accountType: 'work',
      baseUrl: fallbackBaseUrl
    };
  }
};

const normalizeResourceScope = (resource: string) =>
  `${resource.replace(/\/$/, '')}/.default`;

const grantPickerAccess = async (context: OneDriveSessionContext) => {
  if (context.target.accountType === 'personal') {
    console.info('Granting personal OneDrive picker access.', {
      accountType: context.target.accountType,
      baseUrl: context.target.baseUrl
    });

    return await acquireAccessToken({
      appId: context.appId,
      authority: context.authority,
      scopes: [MSAL_SCOPES.oneDriveReadWrite],
      requestAuthority: ONEDRIVE_CONSUMER.authority
    });
  }

  console.info('Granting work OneDrive picker access.', {
    accountType: context.target.accountType,
    baseUrl: context.target.baseUrl
  });

  return await acquireAccessToken({
    appId: context.appId,
    authority: context.authority,
    scopes: [normalizeResourceScope(context.target.baseUrl)]
  });
};

const createPickerConfiguration = ({
  allowVideoAndPdf,
  channelId,
  locale
}: {
  allowVideoAndPdf: boolean;
  channelId: string;
  locale?: string;
}) => {
  const filters = allowVideoAndPdf
    ? MEDIA_UPLOAD_ALL_EXTENSIONS
    : MEDIA_UPLOAD_IMAGE_ONLY_EXTENSIONS;

  return {
    channelId,
    queryString: new URLSearchParams({
      filePicker: JSON.stringify({
        sdk: '8.0',
        entry: {
          oneDrive: {
            files: {}
          }
        },
        authentication: {},
        commands: {
          pick: {
            action: 'select',
            select: {
              urls: {
                download: true
              }
            }
          }
        },
        messaging: {
          origin: window.location.origin,
          channelId
        },
        selection: {
          mode: 'multiple'
        },
        typesAndSources: {
          mode: 'files',
          filters,
          pivots: {
            oneDrive: true,
            recent: true
          }
        }
      }),
      locale: (locale || navigator.language || 'en-US').toLowerCase()
    })
  };
};

const getPickerToken = async (
  command: Pick<OneDrivePickerCommand, 'type' | 'resource'>,
  context: OneDriveSessionContext
) => {
  console.info('OneDrive picker authenticate request.', {
    accountType: context.target.accountType,
    resource: command.resource,
    type: command.type
  });

  if (
    context.target.accountType === 'personal' ||
    isConsumerOneDriveResource(command.resource || '')
  ) {
    console.info('Using consumer OneDrive token flow.', {
      accountType: context.target.accountType,
      resource: command.resource
    });

    return await acquireAccessToken({
      appId: context.appId,
      authority: context.authority,
      scopes: [MSAL_SCOPES.oneDriveReadWrite],
      requestAuthority: ONEDRIVE_CONSUMER.authority
    });
  }

  if (command.type === 'SharePoint' || command.type === 'SharePoint_SelfIssued') {
    if (!command.resource) {
      throw new Error('OneDrive did not provide a SharePoint resource.');
    }

    console.info('Using SharePoint token flow.', {
      resource: command.resource
    });

    return await acquireAccessToken({
      appId: context.appId,
      authority: context.authority,
      scopes: [normalizeResourceScope(command.resource)]
    });
  }

  console.info('Using Graph token flow.', {
    resource: command.resource
  });

  return await acquireAccessToken({
    appId: context.appId,
    authority: context.authority,
    scopes: [MICROSOFT_GRAPH.defaultScope]
  });
};

const getPickedItemDownloadUrl = (item: OneDriveFileItem) =>
  item['@microsoft.graph.downloadUrl'] ||
  item.urls?.download ||
  item.downloadUrl ||
  null;

interface OneDriveGraphItemDetails {
  downloadUrl?: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  durationMs?: number;
}

interface OneDriveGraphItemPayload {
  '@microsoft.graph.downloadUrl'?: string;
  thumbnails?: Array<{ medium?: { url?: string } }>;
  image?: { width?: number; height?: number };
  video?: { width?: number; height?: number; duration?: number };
}

const callOneDriveGraph = async (
  itemUrl: string,
  graphToken: string,
  queryString: string
): Promise<OneDriveGraphItemPayload | null> => {
  const url = queryString ? `${itemUrl}?${queryString}` : itemUrl;
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${graphToken}` }
    });
    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      console.error(
        `[OneDrive] Graph fetch failed (${response.status}) for ${queryString}: ${errorBody}`
      );
      return null;
    }
    const payload = (await response.json()) as OneDriveGraphItemPayload;
    console.info('[OneDrive] Graph response:', payload);
    return payload;
  } catch (error) {
    console.error('[OneDrive] Graph fetch threw.', error);
    return null;
  }
};

const fetchOneDriveItemDetailsViaGraph = async (
  item: OneDriveFileItem,
  context: OneDriveSessionContext
): Promise<OneDriveGraphItemDetails> => {
  console.info('[OneDrive] picked item shape:', item);

  if (!item.parentReference?.driveId) {
    console.error(
      '[OneDrive] item is missing parentReference.driveId, cannot call Graph.',
      item
    );
    return {};
  }

  let graphToken: string;
  try {
    graphToken = await acquireAccessToken({
      appId: context.appId,
      authority: context.authority,
      scopes: [MICROSOFT_GRAPH.defaultScope],
      requestAuthority:
        context.target.accountType === 'personal'
          ? ONEDRIVE_CONSUMER.authority
          : undefined
    });
  } catch (error) {
    console.error('[OneDrive] Graph token acquisition failed.', error);
    return {};
  }

  const itemUrl = `${MICROSOFT_GRAPH.baseUrl}/drives/${item.parentReference.driveId}/items/${item.id}`;
  console.info('[OneDrive] Graph item URL:', itemUrl);

  // Note: don't combine `$select` with `@microsoft.graph.downloadUrl` — Graph
  // strips the annotation when both `$select` and `$expand` are present.
  // The default driveItem response already includes downloadUrl, image, video,
  // size, file, etc., so we only need `$expand` for thumbnails.
  const fullParams = new URLSearchParams();
  fullParams.append('$expand', 'thumbnails($select=medium)');

  const full = await callOneDriveGraph(itemUrl, graphToken, fullParams.toString());
  if (full) {
    return {
      downloadUrl: full['@microsoft.graph.downloadUrl'],
      thumbnailUrl: full.thumbnails?.[0]?.medium?.url,
      width: full.image?.width ?? full.video?.width,
      height: full.image?.height ?? full.video?.height,
      durationMs: full.video?.duration
    };
  }

  // Fallback: bare item request — gets everything Graph returns by default
  // including @microsoft.graph.downloadUrl. Skips thumbnail expansion.
  const minimal = await callOneDriveGraph(itemUrl, graphToken, '');
  if (minimal) {
    return {
      downloadUrl: minimal['@microsoft.graph.downloadUrl'],
      width: minimal.image?.width ?? minimal.video?.width,
      height: minimal.image?.height ?? minimal.video?.height,
      durationMs: minimal.video?.duration
    };
  }

  return {};
};

const mapPickedItemsToCloudFiles = async (
  items: OneDriveFileItem[],
  context: OneDriveSessionContext
): Promise<CloudPickerFile[]> => {
  const validItems = (items || []).filter(
    (item) => item?.id && item?.parentReference?.driveId && !item?.folder
  );

  const details = await Promise.all(
    validItems.map((item) =>
      fetchOneDriveItemDetailsViaGraph(item, context)
    )
  );

  return validItems.map((item, index) => {
    const itemDetails = details[index];
    const resolvedMime =
      item.file?.mimeType || inferMimeFromFilename(item.name);
    const resolveUrl = () => {
      const url =
        getPickedItemDownloadUrl(item) || itemDetails.downloadUrl;
      if (!url) {
        throw new Error(
          'OneDrive picker did not provide a download URL for the selected file.'
        );
      }
      return url;
    };

    return {
      provider: 'onedrive' as const,
      name: item.name,
      size: item.size,
      mimeType: resolvedMime,
      width: itemDetails.width,
      height: itemDetails.height,
      durationMs: itemDetails.durationMs,
      thumbnailUrl: itemDetails.thumbnailUrl,
      getUploadDescriptor: async () => ({ url: resolveUrl() }),
      toBrowserFile: async (onProgress) =>
        await fetchBrowserFileFromUrl(
          resolveUrl(),
          undefined,
          {
            providedName: item.name,
            mimeType: resolvedMime,
            fallbackBaseName: `onedrive-file-${Date.now()}-${index + 1}`
          },
          onProgress
        )
    };
  });
};

const closeWindowSafely = (win: Window | null) => {
  if (win && !win.closed) {
    win.close();
  }
};

const openPickerWindow = () => {
  const win = window.open('', 'Picker', 'width=1080,height=680');
  if (!win) {
    throw new Error('Unable to open the OneDrive picker popup.');
  }

  return win;
};

const postPickerForm = ({
  accessToken,
  url,
  win
}: {
  accessToken: string;
  url: string;
  win: Window;
}) => {
  const form = win.document.createElement('form');
  form.setAttribute('action', url);
  form.setAttribute('method', 'POST');

  const tokenInput = win.document.createElement('input');
  tokenInput.setAttribute('type', 'hidden');
  tokenInput.setAttribute('name', 'access_token');
  tokenInput.setAttribute('value', accessToken);

  form.appendChild(tokenInput);
  win.document.body.appendChild(form);
  form.submit();
};

const waitForPickerResult = async ({
  accessToken,
  channelId,
  context,
  url,
  win
}: {
  accessToken: string;
  channelId: string;
  context: OneDriveSessionContext;
  url: string;
  win: Window;
}) =>
  await new Promise<CloudPickerFile[]>((resolve, reject) => {
    const handlers: PickerPromiseHandlers = { resolve, reject };

    let port: MessagePort | null = null;
    let settled = false;

    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('message', initializeMessageListener);
      if (port) {
        port.close();
      }
      window.clearInterval(closeWatcher);
      callback();
    };

    const finishWithFiles = (files: CloudPickerFile[]) =>
      finish(() => {
        closeWindowSafely(win);
        handlers.resolve(files);
      });

    const finishWithError = (error: Error) =>
      finish(() => {
        closeWindowSafely(win);
        handlers.reject(error);
      });

    const postCommandResult = (
      messageId: string,
      data: Record<string, unknown>
    ) => {
      if (!port) {
        return;
      }

      port.postMessage({
        type: 'result',
        id: messageId,
        data
      });
    };

    const channelMessageListener = async (event: MessageEvent) => {
      const payload = event.data;
      if (!port || !payload) {
        return;
      }

      if (payload.type === 'notification') {
        return;
      }

      if (payload.type !== 'command') {
        return;
      }

      const messageId = payload.id;
      const command: OneDrivePickerCommand = payload.data;

      port.postMessage({
        type: 'acknowledge',
        id: messageId
      });

      switch (command.command) {
        case 'authenticate':
          try {
            const token = await getPickerToken(command, context);
            postCommandResult(messageId, {
              result: 'token',
              token
            });
          } catch (error) {
            postCommandResult(messageId, {
              result: 'error',
              error: {
                code: 'unableToObtainToken',
                message:
                  error instanceof Error
                    ? error.message
                    : 'Unable to obtain OneDrive token.'
              }
            });
            finishWithError(
              error instanceof Error
                ? error
                : new Error('Unable to obtain OneDrive token.')
            );
          }
          break;
        case 'pick':
          postCommandResult(messageId, {
            result: 'success'
          });
          try {
            const files = await mapPickedItemsToCloudFiles(
              command.items || [],
              context
            );
            finishWithFiles(files);
          } catch (error) {
            finishWithError(
              error instanceof Error
                ? error
                : new Error('Unable to use the selected OneDrive item.')
            );
          }
          break;
        case 'close':
          postCommandResult(messageId, {
            result: 'success'
          });
          finishWithFiles([]);
          break;
        default:
          postCommandResult(messageId, {
            result: 'error',
            error: {
              code: 'unsupportedCommand',
              message: command.command
            }
          });
          break;
      }
    };

    const messagePortListener: EventListener = (event) => {
      void channelMessageListener(event as MessageEvent);
    };

    const initializeMessageListener = (event: MessageEvent) => {
      if (!event.source || event.source !== win) {
        return;
      }

      const message = event.data;
      if (message?.type !== 'initialize' || message.channelId !== channelId) {
        return;
      }

      port = event.ports[0];
      port.addEventListener('message', messagePortListener);
      port.start();
      port.postMessage({
        type: 'activate'
      });
    };

    const closeWatcher = window.setInterval(() => {
      if (win.closed) {
        finishWithFiles([]);
      }
    }, 500);

    window.addEventListener('message', initializeMessageListener);

    try {
      postPickerForm({
        accessToken,
        url,
        win
      });
    } catch (error) {
      finishWithError(
        error instanceof Error
          ? error
          : new Error('Unable to initialize OneDrive picker.')
      );
    }
  });

export const clearOneDriveAuthCache = async (
  appId: string,
  authority: string
) => {
  try {
    const app = await getMsalApp(appId, authority);
    app.setActiveAccount(null);
    await app.clearCache();
  } catch (error) {
    console.warn('Failed to clear OneDrive auth cache.', error);
  }
  cachedMsalApp = undefined;
};

export const launchOneDrivePicker = async (
  options: OneDrivePickerOptions
) => {
  // Open the popup synchronously, while we still hold the user gesture from the
  // click. The auth/token steps below are async — a window.open() that runs
  // several awaits after the click gets neutered or blocked by the browser
  // (especially on the silent-token path, where no MSAL login popup intervenes
  // to keep the gesture alive), which leaves the picker stuck at about:blank.
  // We open here, then POST the picker form into this same window once the
  // token is ready.
  const win = openPickerWindow();

  try {
    if (options.forceAccountSelect) {
      await clearOneDriveAuthCache(options.appId, options.authority);
    }

    const target = await resolvePickerTarget(options);
    const context: OneDriveSessionContext = {
      appId: options.appId,
      authority: options.authority,
      target
    };

    console.info('Resolved OneDrive picker target.', target);

    const initialToken = await grantPickerAccess(context);
    const { channelId, queryString } = createPickerConfiguration({
      allowVideoAndPdf: options.allowVideoAndPdf,
      channelId: uuidv4(),
      locale: options.locale
    });

    console.info(
      'Posting OneDrive picker form into the pre-opened popup.',
      target
    );

    return await waitForPickerResult({
      accessToken: initialToken,
      channelId,
      context,
      url: buildOneDrivePickerUrl(target, queryString.toString()),
      win
    });
  } catch (error) {
    closeWindowSafely(win);
    throw error;
  }
};

export const createOneDrivePicker = ({
  appId,
  authority
}: Pick<OneDrivePickerOptions, 'appId' | 'authority'>): CloudPickerAdapter => ({
  launch: async ({ allowVideoAndPdf, locale, forceAccountSelect }) =>
    await launchOneDrivePicker({
      allowVideoAndPdf,
      appId,
      authority,
      locale,
      forceAccountSelect
    }),
  getErrorTranslationKey: (error) => {
    if (isOneDrivePickerError(error)) {
      switch (error.code) {
        case 'drive_not_found':
          return 'lomavisCreator.additionalOptions.onedriveDriveUnavailable';
        case 'access_denied':
          return 'lomavisCreator.additionalOptions.onedrivePermissionDenied';
        default:
          return 'lomavisCreator.additionalOptions.onedriveAuthFailed';
      }
    }

    return 'lomavisCreator.additionalOptions.onedriveAuthFailed';
  }
});
